/**
 * Auto Logout Warning Modal
 * 
 * 은행 홈페이지 스타일 자동 로그아웃 모달
 * - 10분 카운트다운 (MM:SS)
 * - 세션 연장 또는 즉시 로그아웃
 * - 자동 로그아웃 후 알림 + 로그인 유도
 */

import { useLanguage } from '../i18n/LanguageContext';
import { useBodyScrollLock } from '../hooks';

interface AutoLogoutWarningProps {
  isOpen: boolean;
  remainingTime: number;
  onExtend: () => void;
  onLogout: () => void;
}

interface LoggedOutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
}

// 10분 카운트다운 경고 모달
export function AutoLogoutWarning({
  isOpen,
  remainingTime,
  onExtend,
  onLogout,
}: AutoLogoutWarningProps) {
  const { t } = useLanguage();
  
  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  // MM:SS 형식으로 포맷팅
  const minutes = Math.floor(remainingTime / 60000);
  const seconds = Math.floor((remainingTime % 60000) / 1000);
  const timeDisplay = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  
  // 1분 이하면 위험 색상
  const isUrgent = remainingTime <= 60000;
  // 3분 이하면 경고 색상
  const isWarning = remainingTime <= 180000 && !isUrgent;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md mx-4 animate-in fade-in zoom-in duration-200"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="auto-logout-title"
        aria-describedby="auto-logout-desc"
      >
        {/* 아이콘 */}
        <div className="flex justify-center mb-6">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
            isUrgent 
              ? 'bg-red-100 dark:bg-red-900/30' 
              : isWarning 
                ? 'bg-amber-100 dark:bg-amber-900/30'
                : 'bg-blue-100 dark:bg-blue-900/30'
          }`}>
            <svg 
              className={`w-10 h-10 transition-colors ${
                isUrgent 
                  ? 'text-red-600 dark:text-red-400 animate-pulse' 
                  : isWarning
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-blue-600 dark:text-blue-400'
              }`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </div>
        </div>

        {/* 제목 */}
        <h2 
          id="auto-logout-title"
          className="text-xl font-bold text-center text-gray-900 dark:text-white mb-3"
        >
          {t.autoLogout?.title || '세션 만료 경고'}
        </h2>

        {/* 설명 */}
        <p 
          id="auto-logout-desc"
          className="text-center text-gray-600 dark:text-gray-300 mb-6"
        >
          {t.autoLogout?.description || '오랜 시간 활동이 없어 곧 로그아웃됩니다.'}
        </p>

        {/* 카운트다운 (은행 스타일 - 큰 디지털 시계) */}
        <div className="flex justify-center mb-8">
          <div className={`px-8 py-6 rounded-2xl transition-colors ${
            isUrgent 
              ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800' 
              : isWarning
                ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800'
                : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600'
          }`}>
            <div className="text-center">
              <span className={`text-sm font-medium transition-colors ${
                isUrgent 
                  ? 'text-red-600 dark:text-red-400' 
                  : isWarning
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-gray-600 dark:text-gray-400'
              }`}>
                {t.autoLogout?.remaining || '남은 시간'}
              </span>
              <div className={`text-5xl font-bold font-mono mt-2 tracking-wider transition-colors ${
                isUrgent 
                  ? 'text-red-600 dark:text-red-400' 
                  : isWarning
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-gray-900 dark:text-white'
              }`}>
                {timeDisplay}
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3">
          <button
            onClick={onLogout}
            className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 
                       text-gray-700 dark:text-gray-300 font-medium
                       hover:bg-gray-100 dark:hover:bg-gray-700 
                       transition-colors duration-200"
          >
            {t.autoLogout?.logoutNow || '로그아웃'}
          </button>
          <button
            onClick={onExtend}
            autoFocus
            className={`flex-1 px-4 py-3 rounded-xl font-medium
                       transition-all duration-200
                       focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isUrgent
                ? 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 animate-pulse'
                : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
            }`}
          >
            {t.autoLogout?.extend || '계속 사용하기'}
          </button>
        </div>

        {/* 안내 텍스트 */}
        <p className="text-xs text-center text-gray-400 dark:text-gray-500 mt-4">
          {t.autoLogout?.hint || '보안을 위해 30분간 활동이 없으면 자동으로 로그아웃됩니다.'}
        </p>
      </div>
    </div>
  );
}

// 자동 로그아웃 완료 알림 모달 (로그인 유도)
export function LoggedOutModal({
  isOpen,
  onClose,
  onLogin,
}: LoggedOutModalProps) {
  const { t } = useLanguage();
  
  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md mx-4 animate-in fade-in zoom-in duration-200"
        role="alertdialog"
        aria-modal="true"
      >
        {/* 아이콘 */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <svg 
              className="w-10 h-10 text-gray-500 dark:text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
              />
            </svg>
          </div>
        </div>

        {/* 제목 */}
        <h2 className="text-xl font-bold text-center text-gray-900 dark:text-white mb-3">
          {t.autoLogout?.loggedOutTitle || '자동 로그아웃되었습니다'}
        </h2>

        {/* 설명 */}
        <p className="text-center text-gray-600 dark:text-gray-300 mb-8">
          {t.autoLogout?.loggedOutDescription || '보안을 위해 자동으로 로그아웃되었습니다. 계속 사용하시려면 다시 로그인해주세요.'}
        </p>

        {/* 버튼 */}
        <div className="space-y-3">
          <button
            onClick={onLogin}
            autoFocus
            className="w-full px-4 py-3 rounded-xl 
                       bg-blue-600 hover:bg-blue-700 
                       text-white font-medium
                       transition-colors duration-200
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t.autoLogout?.loginAgain || '다시 로그인'}
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-500 dark:text-gray-400 
                       hover:text-gray-700 dark:hover:text-gray-200 
                       transition-colors"
          >
            {t.autoLogout?.closeModal || '닫기'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AutoLogoutWarning;
